import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Database, Play, RotateCcw, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

function Stat({ label, pre, post }) {
  const ok = pre === post;
  return (
    <div className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-center">
      <p className="text-[10px] font-bold uppercase tracking-wide text-[#6E5B51]">{label}</p>
      <p className="text-sm font-extrabold text-[#2D1E18]">{pre} <span className="text-[#6E5B51]">→</span> {post}</p>
      {ok ? <CheckCircle2 size={12} className="mx-auto mt-0.5 text-emerald-600" /> : <AlertTriangle size={12} className="mx-auto mt-0.5 text-amber-600" />}
    </div>
  );
}

export default function BackfillPanel() {
  const qc = useQueryClient();
  const [report, setReport] = useState(null);
  const { data: sandbox, refetch: refetchSandbox } = useQuery({
    queryKey: ["bf-sandbox"], queryFn: async () => (await api.get("/admin/us/backfill/sandbox")).data,
  });

  const apply = useMutation({
    mutationFn: async () => (await api.post("/admin/us/backfill/apply-isolated")).data,
    onSuccess: (d) => { setReport(d); refetchSandbox(); qc.invalidateQueries({ queryKey: ["bf-runs"] }); },
  });
  const rollback = useMutation({
    mutationFn: async () => (await api.post("/admin/us/backfill/rollback")).data,
    onSuccess: () => { setReport(null); refetchSandbox(); },
  });

  const s = report?.summary;
  const exc = report?.exception_counts;
  const hasExc = exc && Object.values(exc).some((n) => n > 0);

  return (
    <div className="mb-8 rounded-2xl border-2 border-[#C85A32]/40 bg-[#FFFaf4] p-4" data-testid="backfill-panel">
      <div className="mb-3 flex items-center gap-2">
        <Database size={18} className="text-[#B04B26]" />
        <h2 className="font-display text-lg font-extrabold text-[#2D1E18]">Backfill — Isolated Sandbox</h2>
      </div>
      <p className="mb-3 flex items-start gap-2 rounded-xl bg-[#FEF6EC] px-3 py-2 text-xs text-[#9A5B1E]">
        <ShieldCheck size={16} className="mt-0.5 shrink-0" />
        <span>Writes ONLY to the isolated <code className="font-mono">bf_sessions</code> sandbox. Legacy collections and the live unified sessions are never touched. Re-running is idempotent. Flags stay off; no member read-swap.</span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => apply.mutate()} disabled={apply.isPending}
          className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#C85A32] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B04B26] disabled:opacity-50"
          data-testid="backfill-apply-btn">
          <Play size={15} /> {apply.isPending ? "Running…" : "Run backfill (sandbox)"}
        </button>
        <button onClick={() => { if (window.confirm("Clear the entire isolated sandbox? Legacy data is untouched.")) rollback.mutate(); }} disabled={rollback.isPending}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-bold text-[#2D1E18] hover:bg-secondary disabled:opacity-50"
          data-testid="backfill-rollback-btn">
          <RotateCcw size={15} /> {rollback.isPending ? "Clearing…" : "Rollback (clear sandbox)"}
        </button>
        <span className="text-xs text-[#6E5B51]" data-testid="backfill-sandbox-count">
          Sandbox now holds <strong>{sandbox?.total_sandbox_sessions ?? 0}</strong> session(s).
        </span>
      </div>

      {report && (
        <div className="mt-4 space-y-3" data-testid="backfill-report">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Sessions" pre={s.pre.sessions} post={s.post.sessions} />
            <Stat label="Dances" pre={s.pre.dances} post={s.post.dances} />
            <Stat label="Activities" pre={s.pre.activities} post={s.post.activities} />
            <Stat label="Dates" pre={s.pre.dates} post={s.post.dates} />
          </div>
          <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${s.member_history_faithful_for_all_classes && !hasExc ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
            {s.member_history_faithful_for_all_classes && !hasExc
              ? <><CheckCircle2 size={16} /> Member-facing history is faithful for all {s.classes_processed} classes. No data loss.</>
              : <><AlertTriangle size={16} /> Review exceptions below — {s.classes_processed} classes processed.</>}
          </div>
          {hasExc && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="backfill-exceptions">
              <p className="mb-1 font-bold uppercase tracking-wide">Exceptions</p>
              <ul className="space-y-0.5">
                {Object.entries(exc).filter(([, n]) => n > 0).map(([k, n]) => <li key={k}>{k.replace(/_/g, " ")}: <strong>{n}</strong></li>)}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-[#6E5B51]">Run id: <span className="font-mono">{report.id}</span> · {report.applied_to}</p>
        </div>
      )}
    </div>
  );
}
